import {useOptimisticSearchParams} from 'nuqs/adapters/react-router/v7';
import GeoJSON from 'ol/format/GeoJSON';
import {useCallback} from 'react';
import {useNavigate} from 'react-router';
import {useAppSelector} from './redux.ts';
import {
  mergeWithDefaults,
  useGetConfigurationCollections,
  useGetConfigurations,
} from '@src/api/configurations';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import {AppLocationState} from '@src/types/appLocation.ts';

export const useMapExtentHandler = () => {
  const navigate = useNavigate();
  const search = useOptimisticSearchParams();
  const showingAOIfeature = useAppSelector(state => state.map.feature);
  const {configurationId, collectionId, setCollectionId, layerId, serialize} =
    useVisualizationParams();
  const {collections} = useGetConfigurationCollections(configurationId);
  const {data: configurations = []} = useGetConfigurations({
    select: mergeWithDefaults,
  });

  const handleComposedMapExtentChange = useCallback(
    async (
      newCollectionId: string | null = collectionId,
      newLayerId: string | null = layerId,
    ) => {
      const selectedCollections = collections.find(
        col => col.id === newCollectionId,
      );
      const collectionExtentBbox =
        selectedCollections?.extent.spatial?.bbox?.[0];
      const configurationAoi = configurations.find(
        config => configurationId === config.id,
      )?.areaOfInterest;
      const geo =
        configurationAoi && new GeoJSON().readGeometry(configurationAoi);
      const configurationExtent = geo?.getExtent();
      const mapExtent = configurationExtent ?? collectionExtentBbox;

      if (!mapExtent || showingAOIfeature) {
        await setCollectionId(newCollectionId, newLayerId);
        return;
      }

      const currentSearchParams = Object.fromEntries(
        (search ?? new URLSearchParams()).entries(),
      );

      const initialVisParams = Object.fromEntries(
        new URLSearchParams(
          serialize({
            configurationId,
            collectionId: newCollectionId,
            layerId: newLayerId,
          }),
        ).entries(),
      );

      const allSearchParams = new URLSearchParams({
        ...currentSearchParams,
        ...initialVisParams,
      })
        .toString()
        .replace('%2C', ',');

      navigate(
        {
          pathname: '.',
          search: allSearchParams,
        },
        {
          state: {
            mapExtent: {bbox: mapExtent},
          } satisfies AppLocationState,
          replace: true,
        },
      );
      await setCollectionId(newCollectionId, newLayerId);
    },
    [
      collectionId,
      collections,
      configurationId,
      configurations,
      layerId,
      navigate,
      search,
      serialize,
      setCollectionId,
      showingAOIfeature,
    ],
  );

  return handleComposedMapExtentChange;
};
